// Einstellungen: Gruppe „Veranstaltungen" (DCP-2228 Folge-Feature)
//
// Ein Hauptpunkt mit zwei Unterpunkten — Mailingdaten und Veranstaltungstexte. Bewusst
// als Karte mit zwei Verweiszeilen und nicht als Klapp- oder Reiterwerk: bei zwei
// Unterpunkten wäre beides mehr Bauwerk als Nutzen, und die Zeilen SIND die Navigation.
//
// Die Anzahlen stehen dran, damit man auf einen Blick sieht, ob überhaupt etwas
// hinterlegt ist — bei einer leeren Vorlagenliste ist genau das die Frage, die man vor
// dem Öffnen hat.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail, FileText, ShieldX, CreditCard, Upload, HandCoins, Landmark } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useMe } from '@/hooks/usePermissions';
import {
  useMailings,
  useEventTexts,
  useCancellationTerms,
  usePaymentNoteTemplates,
  useFinancialSupport,
} from '@/services/event-service';
import { SettlementOverrideDialog } from './SettlementOverrideDialog';

export function EventSettingsCard() {
  const { t } = useTranslation('settings');
  const { data: me } = useMe();
  const [settlementOverrideOpen, setSettlementOverrideOpen] = useState(false);

  const { data: mailings = [] } = useMailings();
  const { data: texts = [] } = useEventTexts();
  const { data: terms = [] } = useCancellationTerms();
  const { data: paymentNoteTemplates = [] } = usePaymentNoteTemplates();
  const { data: financialSupport = [] } = useFinancialSupport();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('events.title')}</CardTitle>
        <CardDescription>{t('events.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          <SettingsRow
            to="/settings/event-import"
            icon={<Upload className="h-4 w-4 text-muted-foreground" />}
            label={t('events.import')}
            hint={t('events.importHint')}
          />
          <SettingsRow
            to="/settings/mailings"
            icon={<Mail className="h-4 w-4 text-muted-foreground" />}
            label={t('events.mailings')}
            hint={t('events.mailingsHint')}
            count={t('mailings.current', { count: mailings.length })}
          />
          <SettingsRow
            to="/settings/financial-support"
            icon={<HandCoins className="h-4 w-4 text-muted-foreground" />}
            label={t('events.financialSupport')}
            hint={t('events.financialSupportHint')}
            count={t('financialSupport.count', { count: financialSupport.length })}
          />
          <SettingsRow
            to="/settings/event-texts"
            icon={<FileText className="h-4 w-4 text-muted-foreground" />}
            label={t('events.texts')}
            hint={t('events.textsHint')}
            count={t('eventTexts.count', { count: texts.length })}
          />
          <SettingsRow
            to="/settings/payment-note-templates"
            icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
            label={t('events.paymentNoteTemplates')}
            hint={t('events.paymentNoteTemplatesHint')}
            count={t('paymentNoteTemplates.count', { count: paymentNoteTemplates.length })}
          />
          <SettingsRow
            to="/settings/cancellation-terms"
            icon={<ShieldX className="h-4 w-4 text-muted-foreground" />}
            label={t('events.cancellationTerms')}
            hint={t('events.cancellationTermsHint')}
            count={t('cancellationTerms.count', { count: terms.length })}
          />
          {/*
            Anmeldebedingungen-Vorlagen (Position 7 in der vom Nutzer
            vorgegebenen Reihenfolge, 20.09.2026): aktuell ausgeblendet, wird
            nicht benötigt. Route (/settings/registration-note-templates),
            Seite und Datenhaltung bleiben unangetastet — bei Bedarf hier
            wieder einkommentieren, dazu `ClipboardList` in den lucide-react-
            Import oben und `useRegistrationNoteTemplates`
            (@/services/event-service) + die zugehörige
            const-Deklaration wieder ergänzen.
            <SettingsRow
              to="/settings/registration-note-templates"
              icon={<ClipboardList className="h-4 w-4 text-muted-foreground" />}
              label={t('events.registrationNoteTemplates')}
              hint={t('events.registrationNoteTemplatesHint')}
              count={t('registrationNoteTemplates.count', { count: registrationNoteTemplates.length })}
            />
          */}
          {me?.canOverrideSettlementMode && (
            <SettingsButtonRow
              icon={<Landmark className="h-4 w-4 text-muted-foreground" />}
              label={t('events.settlementOverride')}
              hint={t('events.settlementOverrideHint')}
              onClick={() => setSettlementOverrideOpen(true)}
            />
          )}
        </div>
      </CardContent>
      <SettlementOverrideDialog
        open={settlementOverrideOpen}
        onOpenChange={setSettlementOverrideOpen}
        settlementMode={me?.settlementMode}
      />
    </Card>
  );
}

interface RowProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  hint: string;
  count?: string;
}

/** Eine Verweiszeile. Die ganze Zeile ist der Verweis, nicht nur der Text. */
function SettingsRow({ to, icon, label, hint, count }: RowProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 py-3 first:pt-0 last:pb-0 hover:bg-muted/40"
    >
      {icon}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="truncate text-xs text-muted-foreground">{hint}</p>
      </div>
      {count && <span className="whitespace-nowrap text-xs text-muted-foreground">{count}</span>}
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}

interface ButtonRowProps {
  icon: React.ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
}

/**
 * Wie SettingsRow, aber öffnet ein Modal statt zu navigieren (kein Bestand
 * zu verwalten, siehe Dateikopf-Kommentar zu SettlementOverrideDialog).
 */
function SettingsButtonRow({ icon, label, hint, onClick }: ButtonRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 py-3 text-left first:pt-0 last:pb-0 hover:bg-muted/40"
    >
      {icon}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="truncate text-xs text-muted-foreground">{hint}</p>
      </div>
    </button>
  );
}
